import { DetectionZone, DetectionZoneType, Facility, OperationMode, Vector2 } from "@app/shared";
import { PassSide, sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import { MAX_ZONE_LENGTH_M, MAX_ZONE_WIDTH_M, MIN_ZONE_DIM_M } from "@app/shared/src/helpers/sidePassGeometry";

/**
 * The protected zone's name.
 *
 * This used to encode where the truck sat on the road ("Attenuator Zone:right"), which caused two
 * problems. It confused operators, who read the dropdown as a claim about the truck's parking spot
 * rather than about which side traffic uses; and a `:left`/`:right` suffix is indistinguishable
 * from the side-pass naming convention, so every non-center truck's collision zone was filed as a
 * side pass (see zoneNameHelpers.ts). The diagram now always draws the truck centred, with traffic
 * assumed on both sides, and the name carries no position at all.
 *
 * Zones saved before this keep their old ":left"/":center"/":right" suffix until they are next
 * saved. That is harmless — nothing reads a position back out, and the side-pass matcher is
 * anchored so it cannot mistake them.
 */
export const ATTENUATOR_ZONE_NAME = "Attenuator Zone";

// --- Real-world dimensions (meters) ---
// TL-2 Scorpion: 2.44m wide. getting dimensions as well.
export const TRAILER_WIDTH_M = 2.44;
export const TRAILER_LENGTH_M = 6;

// default cushion (attenuator module tile area) dimensions
export const DEFAULT_WIDTH = 2;
export const DEFAULT_LENGTH = 4;
// Shared with the consoles that write this footprint — see sidePassGeometry.
export const MIN_DIM = MIN_ZONE_DIM_M;
export const MAX_WIDTH = MAX_ZONE_WIDTH_M;
export const MAX_LENGTH = MAX_ZONE_LENGTH_M;

// --- Layout visual constants ---
export const ROAD_WIDTH = 1024;
export const ROAD_HEIGHT = 1536;

const TRUE_PX_PER_METER = 75 / 3.7; // ~20 px/m (physical truth)
export const DISPLAY_SCALE = 1.0;
export const PX_PER_METER = Math.round(TRUE_PX_PER_METER * DISPLAY_SCALE); // ~20 px/m

/** Centre of the road in the diagram. The truck is always drawn here. */
export const ROAD_CENTER_X = 505;

const TRUCK_VISUAL_SCALE = 1.4;
export const TRUCK_W = Math.round(TRAILER_WIDTH_M * PX_PER_METER * TRUCK_VISUAL_SCALE);
export const TRUCK_H = Math.round(TRAILER_LENGTH_M * PX_PER_METER * TRUCK_VISUAL_SCALE);
export const TRUCK_Y = 1320;

// Cable/tow bar connecting truck to cushion area — also scaled with the truck.
export const CABLE_IMG_W = Math.round(1.2 * PX_PER_METER * TRUCK_VISUAL_SCALE);
export const CABLE_IMG_H = Math.round(0.15 * PX_PER_METER * TRUCK_VISUAL_SCALE);

const TRAILER_OFFSET = 1;

// --- dzone point generation ---
export function buildAttenuatorZonePoints(width: number, length: number): Vector2[] {
    const hw = width / 2;
    const nearY = -TRAILER_OFFSET;
    const farY = -(TRAILER_OFFSET + length);
    return [new Vector2({ x: -hw, y: nearY }), new Vector2({ x: hw, y: nearY }), new Vector2({ x: hw, y: farY }), new Vector2({ x: -hw, y: farY })];
}

export function getDimensionsFromZone(zone: DetectionZone): { width: number; length: number } {
    const pts = zone.points;
    if (!pts || pts.length === 0) return { width: DEFAULT_WIDTH, length: DEFAULT_LENGTH };

    const xs = pts.map((p) => p.x ?? 0);
    const ys = pts.map((p) => p.y ?? 0);
    const width = Math.max(...xs) - Math.min(...xs);
    const length = Math.max(...ys) - Math.min(...ys);

    return {
        width: width > 0 ? width : DEFAULT_WIDTH,
        length: length > 0 ? length : DEFAULT_LENGTH,
    };
}

export function createDefaultAttenuatorZone(facilityId: string): DetectionZone {
    const zone = new DetectionZone();
    zone.facilityId = facilityId;
    zone.name = ATTENUATOR_ZONE_NAME;
    zone.zoneType = DetectionZoneType.ParkingArea;
    zone.points = buildAttenuatorZonePoints(DEFAULT_WIDTH, DEFAULT_LENGTH);
    return zone;
}

export function isAttenuatorFacility(facility: Facility): boolean {
    const devices = facility.devices;
    if (!devices || devices.length === 0) return false;
    return devices.every((d) => d.mode === OperationMode.Attenuator_Truck);
}

export function hasAttenuatorDevice(facility: Facility): boolean {
    return !!facility.devices?.some((d) => d.mode === OperationMode.Attenuator_Truck);
}

/**
 * The protected zone — the impending-collision cushion behind the truck.
 *
 * NOT simply the first zone: a truck also carries "Side Pass:left"/"Side Pass:right" zones (created
 * by the fleet console and the in-cab app), and whichever happens to sort first used to be read as
 * the cushion. Editing then renamed a side-pass zone to "Attenuator Zone" and repointed it, which
 * silently turned that side's pass alert into a second collision trigger.
 */
export function findAttenuatorZone(facility: Facility): DetectionZone | undefined {
    return facility.detectionZones?.find((z) => !sideOfZoneName(z.name));
}

/** The side-pass zones flanking the truck, when the fleet has set them up. */
export function findSidePassZone(facility: Facility, side: PassSide): DetectionZone | undefined {
    return facility.detectionZones?.find((z) => sideOfZoneName(z.name) === side);
}

/**
 * A zone's footprint as a diagram rectangle.
 *
 * The cushion itself is drawn from its width/length with its near edge pinned at the tow cable, so
 * this maps a zone's actual points through that same transform — near edge (y = -TRAILER_OFFSET) at
 * the cable, PX_PER_METER scale, x centred on the truck. Side-pass zones drawn this way land flush
 * against the cushion in true proportion, because their geometry is measured off the protected zone.
 */
export function zoneToSvgRect(
    points: { x?: number; y?: number }[] | undefined,
): { x: number; y: number; width: number; height: number } | undefined {
    if (!points || points.length < 3) return undefined;
    const xs = points.map((p) => p.x ?? 0);
    const ys = points.map((p) => p.y ?? 0);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const cableBaseY = TRUCK_Y - CABLE_IMG_W;
    return {
        x: ROAD_CENTER_X + minX * PX_PER_METER,
        y: cableBaseY + (minY + TRAILER_OFFSET) * PX_PER_METER,
        width: (maxX - minX) * PX_PER_METER,
        height: (maxY - minY) * PX_PER_METER,
    };
}

// layout positioning
export function computeLayoutPositions(width: number, length: number) {
    const cx = ROAD_CENTER_X;
    const truckX = cx - TRUCK_W / 2;
    const truckY = TRUCK_Y;

    const cableVisualH = CABLE_IMG_W;
    const cableVisualW = CABLE_IMG_H;
    const cableX = cx - CABLE_IMG_W / 2;
    const cableY = truckY - CABLE_IMG_H;

    const cushionW = width * PX_PER_METER;
    const cushionH = length * PX_PER_METER;
    const cushionX = cx - cushionW / 2;
    const cushionY = truckY - cableVisualH - cushionH;

    return { cx, truckX, truckY, cableX, cableY, cableVisualW, cableVisualH, cushionX, cushionY, cushionW, cushionH };
}

const DEG2RAD = Math.PI / 180;

export function facilityToSvgPoint(
    pos: { x: number; y: number },
    device: { positionX: number; positionY: number; headingDegrees: number },
    devSvgX: number,
    devSvgY: number,
): { x: number; y: number } {
    const dx = pos.x - device.positionX;
    const dy = pos.y - device.positionY;
    const h = device.headingDegrees * DEG2RAD;

    const forwardRange = dx * Math.sin(h) - dy * Math.cos(h);
    const crossRange = dx * Math.cos(h) + dy * Math.sin(h);

    return {
        x: devSvgX + crossRange * PX_PER_METER,
        y: devSvgY - forwardRange * PX_PER_METER,
    };
}

// Device FOV

export function buildFovConePath(
    devX: number,
    devY: number,
    deviceHeight: number,
    tiltDeg: number,
    azimuthFovDeg = 90,
    elevationFovDeg = 30,
    maxRangeM = 75,
): string {
    const declinationRad = DEG2RAD * (90 - Math.abs(tiltDeg));
    const fovWRad = DEG2RAD * azimuthFovDeg;
    const fovHRad = DEG2RAD * elevationFovDeg;
    const maxRangePx = maxRangeM * PX_PER_METER;

    const segments = 40;
    const points: string[] = [];

    for (let i = 0; i < segments; i++) {
        const theta = (2 * Math.PI * i) / segments;
        const ax = (fovWRad / 2) * Math.cos(theta);
        const ay = (fovHRad / 2) * Math.sin(theta);

        let effDeclination = declinationRad + ay;
        effDeclination = Math.min(effDeclination, DEG2RAD * 89);

        const cosDecl = Math.cos(effDeclination);
        if (cosDecl <= 0.001) continue;

        const t = deviceHeight / cosDecl;
        let gx = Math.sin(ax) * t * PX_PER_METER;
        let gy = Math.sin(effDeclination) * t * PX_PER_METER;

        const dist = Math.sqrt(gx * gx + gy * gy);
        if (dist > maxRangePx) {
            const scale = maxRangePx / dist;
            gx *= scale;
            gy *= scale;
        }

        points.push(`${i === 0 ? "M" : "L"} ${devX + gx} ${devY - gy}`);
    }

    points.push("Z");
    return points.join(" ");
}
